import React, { useCallback, useContext, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, Stack, Typography, Badge, Box } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { ArchivedManualsContext } from '../context';
import { QuickSearch } from 'components/_atoms';
import datetimeHelper from 'helpers/datetimeHelper';
import { ArchivedManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';

type ArchivedManualsUrlParams = {
  search?: string | null;
};

const defaultParams: ArchivedManualsUrlParams = {
  search: null,
};

const columnHelper = createColumnHelper<ArchivedManualItem>();

const ArchivedManualsList: React.FC = () => {
  const { manuals, manualsQuery, params, updateParams } = useContext(ArchivedManualsContext);
  const [urlParams, setUrlParams] = useUrlParams(defaultParams);

  const handleUpdateParams = useCallback(
    (updatedParams: ArchivedManualsUrlParams) => {
      setUrlParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setUrlParams]
  );

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.manuals.properties.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Stack>
              <Link href={`./${row.id}`}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 'semibold' }}
                >
                  {row.name}
                </Typography>
              </Link>
              <Typography>{`${datetimeHelper.formatDate(row.fromDate, { format: 'short' })} - ${datetimeHelper.formatDate(row.toDate, { format: 'short' })}`}</Typography>
            </Stack>
          );
        },
      },
      vertical: {
        header: t('policy.manuals.properties.vertical'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.vertical?.value}</Typography>;
        },
      },
      manualType: {
        header: t('policy.manuals.properties.manualType'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.manualType?.value}</Typography>;
        },
      },
    }),
    [t]
  );

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: manualsQuery.hasNextPage,
      isFetchingNextPage: manualsQuery.isFetchingNextPage,
      fetchNextPage: manualsQuery.fetchNextPage,
      isError: manualsQuery.isError,
      isLoading: manualsQuery.isLoading,
      error: manualsQuery.error,
      data: {
        pages:
          manualsQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [manualsQuery]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.archivedManuals.title'),
        subtitle: t('policy.archivedManuals.subtitle'),
        titleBadge:
          (manualsQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={manualsQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
            >
              <QuickSearch
                id='keyword_search'
                initialValue={urlParams.search || ''}
                onChange={(val) => handleUpdateParams({ search: val })}
                placeholder={t('archivedManuals.search.placeholder')}
                minWidth={400}
              />
            </Stack>
          }
        />
        <GridSection
          columns={columns}
          query={gridViewQuery}
          data={manuals}
        />
      </Box>
    </CommonLayout>
  );
};

export default ArchivedManualsList;
